import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { Users, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES, getStateProvinceOptions } from '@/utils/locationData';

interface CreateTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onTeamCreated: () => void;
}

export const CreateTeamDialog: React.FC<CreateTeamDialogProps> = ({
  open,
  onOpenChange,
  userId,
  onTeamCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    team_name: '',
    team_description: '',
    sport_type: '',
    country: '',
    state_province: '',
    city: '',
  });

  const sportOptions = [
    'Football', 'Basketball', 'Soccer', 'Baseball', 'Tennis',
    'Golf', 'Swimming', 'Track & Field', 'Volleyball', 'Hockey',
    'Lacrosse', 'Rugby', 'Cricket', 'Other'
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      // Reset state_province when country changes
      if (field === 'country') {
        return { ...prev, [field]: value, state_province: '' };
      }
      return { ...prev, [field]: value };
    });
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.team_name.trim()) {
      toast.error('Please enter a team name');
      return;
    }

    setLoading(true);
    try {
      const slug = generateSlug(formData.team_name);
      
      // Check if slug already exists
      const { data: existingTeam } = await supabase
        .from('teams')
        .select('team_id')
        .eq('slug', slug)
        .single();

      const finalSlug = existingTeam ? `${slug}-${Date.now()}` : slug;

      // Create the team
      const { data: newTeam, error: createError } = await supabase
        .from('teams')
        .insert({
          team_name: formData.team_name,
          team_description: formData.team_description || null,
          sport_type: formData.sport_type || null,
          country: formData.country || null,
          state_province: formData.state_province || null,
          city: formData.city || null,
          slug: finalSlug,
          created_by: userId,
        })
        .select('team_id')
        .single();

      if (createError) {
        console.error('Error creating team:', createError);
        toast.error('Failed to create team. Please try again.');
        return;
      }

      // Update internal_user_profiles connected_teams array
      const { data: profileData } = await supabase
        .from('internal_user_profiles')
        .select('connected_teams')
        .eq('user_id', userId)
        .single();

      const currentTeams = (profileData?.connected_teams as number[]) || [];
      if (newTeam && !currentTeams.includes(newTeam.team_id)) {
        await supabase
          .from('internal_user_profiles')
          .update({ 
            connected_teams: [...currentTeams, newTeam.team_id] 
          })
          .eq('user_id', userId);
      }

      toast.success(`Team "${formData.team_name}" created successfully!`);
      
      // Reset form
      setFormData({
        team_name: '',
        team_description: '',
        sport_type: '',
        country: '',
        state_province: '',
        city: '',
      });
      
      onTeamCreated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error('Failed to create team. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-sports-green" />
            Create New Team
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Set up your team and start managing it right away.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="team_name" className="text-gray-300">Team Name *</Label>
            <Input
              id="team_name"
              value={formData.team_name}
              onChange={(e) => handleInputChange('team_name', e.target.value)}
              className="bg-gray-700 border-gray-600 text-white"
              placeholder="Enter team name"
              required
            />
          </div>

          <div>
            <Label htmlFor="sport_type" className="text-gray-300">Sport</Label>
            <Select
              value={formData.sport_type}
              onValueChange={(value) => handleInputChange('sport_type', value)}
            >
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select a sport" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {sportOptions.map((sport) => (
                  <SelectItem key={sport} value={sport} className="text-white hover:bg-gray-600">
                    {sport}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="country" className="text-gray-300">Country</Label>
            <Select
              value={formData.country}
              onValueChange={(value) => handleInputChange('country', value)}
            >
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {COUNTRIES.map((country) => (
                  <SelectItem key={country} value={country} className="text-white hover:bg-gray-600">
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="state_province" className="text-gray-300">State/Province</Label>
              {formData.country ? (
                <Select
                  value={formData.state_province}
                  onValueChange={(value) => handleInputChange('state_province', value)}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select state/province" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600 max-h-60">
                    {getStateProvinceOptions(formData.country).map((region) => (
                      <SelectItem key={region} value={region} className="text-white hover:bg-gray-600">
                        {region}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-gray-400 text-sm h-10 flex items-center">
                  Select country first
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="city" className="text-gray-300">City</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Enter city"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="team_description" className="text-gray-300">Description</Label>
            <Textarea
              id="team_description"
              value={formData.team_description}
              onChange={(e) => handleInputChange('team_description', e.target.value)}
              className="bg-gray-700 border-gray-600 text-white min-h-[80px]"
              placeholder="Brief description of your team"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-sports-green text-black hover:bg-green-600"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4 mr-2" />
                  Create Team
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
